import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from '../../store/Home/authSlice';
import { getParentCorpLegalEntities, staffingSelection, getCookie, setWorkspace } from '../../services/api';
import { preserveRedirectInPath, buildFinalRedirect } from '../../utils/redirect';
import { PARAMS } from '../../params';

const LegalEntitySelectionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    // Auth slice shape: token is at top-level `accessToken` (camelCase), NOT
    // at userData.accesstoken — that path doesn't exist in the slice.
    const { userData, accessToken, refreshToken } = useSelector((state: any) => state.auth);
    const email: string = userData?.email || '';
    const selectedCorp = userData?.selected_corp;
    const corporationDetails = userData?.corporation_details || [];

    const [legalEntities, setLegalEntities] = useState<any[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Set right before a hard `window.location.href` redirect. Unlike
    // navigate(), a hard redirect doesn't unmount this component — the
    // browser can take a while (especially on slow networks) to actually
    // leave the page. Without this flag, the `finally` in fetchLegalEntities
    // flips loading back to false in the meantime and the picker UI
    // flashes on screen until the browser catches up.
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!selectedCorp || !email) {
            navigate('/signin-new');
            return;
        }
        fetchLegalEntities();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedEntity && !submitting) {
                handleProceed();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedEntity, submitting]);

    const fetchLegalEntities = async () => {
        setLoading(true);
        try {
            const response = await getParentCorpLegalEntities(email, selectedCorp.id);
            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                setLegalEntities(response.data);
                // Auto-select if only one
                if (response.data.length === 1) {
                    await proceedWithEntity(response.data[0]);
                }
            } else {
                // No legal entities → proceed without legal_entity_id
                await proceedWithEntity(null);
            }
        } catch (err) {
            setError('Failed to fetch legal entities');
        } finally {
            setLoading(false);
        }
    };

    const proceedWithEntity = async (entity: any | null) => {
        setSubmitting(true);
        const staffingResponse = await staffingSelection({
            email,
            corporation_id: selectedCorp.id,
            ...(entity ? { legal_entity_id: entity.id } : {})
        });

        if (staffingResponse && !staffingResponse.error) {
            const userinfo = {
                ...staffingResponse.userinfo,
                parent_corp_id: selectedCorp.id,
                ...(entity ? { parent_legal_entity_id: entity.id, parent_legal_entity_name: entity.name } : {}),
            };
            localStorage.setItem(PARAMS.USER_STORAGE_KEY, JSON.stringify(userinfo));

            dispatch(setData({
                data: {
                    userinfo,
                    google_map_key: staffingResponse.google_map_key,
                    map_id: staffingResponse.map_id,
                    corporation_details: corporationDetails,
                    email
                },
                // getCookie returns null for HttpOnly cookies on deployed
                // (.veytan.com). Without the userData?.accesstoken fallback,
                // this dispatch would WIPE the access token that signin
                // already put in Redux — which then makes workspace-selection's
                // buildFinalRedirect skip the ?token= append for localhost
                // redirects, breaking the localhost auth handoff.
                accesstoken: getCookie(PARAMS.TOKEN_STORAGE_KEY) || accessToken || '',
                refreshtoken: getCookie(PARAMS.TOKEN_STORAGE_KEY_REFRESH) || refreshToken || ''
            }));

            const staffingDetails = userinfo?.staffing_corp_access_list?.staffing_details || [];
            if (staffingDetails.length > 0) {
                navigate(preserveRedirectInPath('/workspace-selection', location), { state: { showBack: true } });
            } else {
                // staffing_corp_access_list is null/empty → no workspace selection
                // screen needed. Terminal redirect — buildFinalRedirect handles the
                // localhost token gate and DEFAULT_REDIRECT_URL fallback in one place.
                // Token source: prefer Redux (signin response), fall back to
                // getCookie which returns null for HttpOnly cookies on deployed
                // but works in the legacy localhost JS-cookie path.
                const params = new URLSearchParams(location.search);
                const baseRedirect = params.get('redirect') || PARAMS.DEFAULT_REDIRECT_URL;
                // Read top-level `accessToken` from auth slice, not userData
                // (that path doesn't exist in the slice shape).
                const token = accessToken || getCookie(PARAMS.TOKEN_STORAGE_KEY);

                // Carry parentCorpData as the selected workspace so the dashboard
                // knows the active corp — mirrors the parentCorpData branch in
                // handleUserLogin (services/api.ts).
                const parentCorpData = userinfo?.parentCorpData;
                const workspaceData = parentCorpData
                    ? {
                        workspace: { type: 'corporation', corporation_ids: [], legal_entity_ids: [], svc_cat_id: '', svc_cat_details: {} },
                        client: {
                            clt_corporation_details: {
                                id: parentCorpData.id,
                                full_name: parentCorpData.full_name,
                                logo_url: Array.isArray(parentCorpData.logo_url)
                                    ? parentCorpData.logo_url[0]?.presigned_url || null
                                    : parentCorpData.logo_url || null
                            }
                        },
                        corporation_id: parentCorpData.id,
                        parent_corp_id: parentCorpData.id,
                        // hrms-01 primes its Redux userinfo straight from this URL param and
                        // reads parent_legal_entity_id off it to attach as `parent_le_id` on
                        // every decodeToken call — must be top-level, matching the shape
                        // already used by workspace-selection/page.tsx's handleCorporationSelect.
                        parent_legal_entity_id: userinfo?.parent_legal_entity_id || null,
                        parent_legal_entity_name: userinfo?.parent_legal_entity_name || null,
                        legal_entity_id: '',
                        svc_cat_id: ''
                    }
                    : undefined;

                // Persist workspace context to BE so /decodeToken on hrms-01 can
                // merge it into userinfo — without this cookie, hrms-01 has no
                // active corp to resolve and bounces the session back to
                // signin-new. Mirrors handleCorporationSelect (workspace-selection/page.tsx).
                if (workspaceData) {
                    const setRes = await setWorkspace(workspaceData);
                    if (setRes?.error) {
                        console.warn('setWorkspace failed (continuing anyway):', setRes.message);
                    }
                }

                setRedirecting(true);
                window.location.href = buildFinalRedirect(baseRedirect, {
                    token: token || undefined,
                    workspaceData,
                });
            }
        } else {
            setError('Failed to complete login. Please try again.');
            setSubmitting(false);
        }
    };

    const handleProceed = async () => {
        if (!selectedEntity || submitting) return;
        await proceedWithEntity(selectedEntity);
    };

    if (loading || redirecting) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-[#F9FAFB]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-[#F9FAFB]">
                <p className="text-red-500">{error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9FAFB] relative">
            <div
                className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                    backgroundImage: 'url(/assets/login/workspace-pattern.svg)',
                    backgroundRepeat: 'repeat'
                }}
            />

            <div className="absolute top-0 left-0 w-full text-2xl text-[#6941C6] font-[Montserrat Alternates] font-bold leading-normal pt-6 pl-9 tracking-[-1.12px]">
                Veytan
            </div>

            <div className="flex justify-center items-center min-h-screen">
                <div className="w-full max-w-[480px] px-4 relative z-10">
                    <div className="w-full bg-white rounded-2xl shadow-[0px_0px_8px_rgba(0,0,0,0.03)]">
                        <div className="px-6 pt-6 pb-4">
                            <h1 className="text-[28px] font-semibold text-[#101828]">Legal Entity</h1>
                            <p className="text-base text-[#475467]">Choose a legal entity to continue</p>
                        </div>

                        <div className="px-4 space-y-1">
                            {legalEntities.map((entity: any) => (
                                <div
                                    key={entity.id}
                                    className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
                                        selectedEntity?.id === entity.id ? 'bg-[#F9FAFB]' : ''
                                    }`}
                                    onClick={() => setSelectedEntity(entity)}
                                >
                                    <div className="flex items-center justify-between py-2.5 px-2">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-shrink-0">
                                                <Avatar
                                                    src={entity.logo_url || '/assets/login/company-default.svg'}
                                                    size={32}
                                                    className="bg-[#F4EBFF]"
                                                    style={{ border: '1px solid #F9F5FF' }}
                                                />
                                            </div>
                                            <p className="text-sm font-medium text-[#101828]">{entity.name}</p>
                                        </div>
                                        <RightOutlined className="text-[#667085]" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="p-4">
                            <button
                                onClick={handleProceed}
                                disabled={!selectedEntity || submitting}
                                className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
                                    selectedEntity && !submitting
                                        ? 'bg-[#7F56D9] text-white hover:bg-[#6941C6]'
                                        : 'bg-[#F2F4F7] text-[#D0D5DD] cursor-not-allowed'
                                }`}
                            >
                                {submitting ? 'Loading...' : 'Proceed to login'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LegalEntitySelectionPage;
